/**
 * authStore — single point of access for the auth token.
 *
 * SECURITY MODEL (Phase 2, Jul 2026):
 *   - Primary auth transport is the HttpOnly `access_token` cookie set by the
 *     backend on /auth/login and /auth/register. It is NOT readable by JS
 *     and flows automatically on same-origin XHR + WebSocket handshakes.
 *   - This module still holds an in-memory JWT copy, populated ONCE per tab
 *     lifetime from the /auth/me response. It is used ONLY for the WebSocket
 *     query-string handshake (some proxies strip cookies from WS upgrade
 *     requests, so query-string auth is the resilient primary transport for
 *     realtime). The in-memory copy is:
 *       * cleared on logout,
 *       * lost on tab close (no XSS-durable exposure),
 *       * lost on hard reload but re-hydrated via /auth/me on next boot.
 *   - No page component may read localStorage / sessionStorage for auth.
 *
 * VAULT: The vault_session cookie is HttpOnly and NEVER touches JS.
 */

let _token = null;
const listeners = new Set();

function _emit() {
  for (const fn of listeners) {
    try { fn(); } catch (e) { console.warn("[authStore] listener threw", e); }
  }
}

export function getToken() {
  return _token;
}

export function setToken(token) {
  _token = token || null;
  _emit();
}

export function clearToken() {
  _token = null;
  _emit();
}

export function hasToken() {
  return !!_token;
}

export function onTokenChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Build a WebSocket URL for the given API path, appending the current
 * in-memory token as a query-string param. Returns null if no token — caller
 * should not open the WS in that case (cookie-only WS auth is supported by
 * the backend, but preview proxies may drop cookies; query-string is safer).
 */
export function wsUrlWithAuth(path) {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return null;
  const wsBase = base.replace(/^http/i, "ws");
  const abs = `${wsBase}/api/ws${path.startsWith("/") ? path : `/${path}`}`;
  if (!_token) return null;
  const sep = abs.includes("?") ? "&" : "?";
  return `${abs}${sep}token=${encodeURIComponent(_token)}`;
}
